// LinkTapsView.jsx
import React, { useState, useCallback } from 'react';
import { Box, Typography, Button, Tooltip } from '@mui/material';
import { KeyboardArrowLeftIcon, InfoIcon, CalendarTodayIcon } from 'icons/materialIcons';
import LinkTapsPieChart from './LinkTapsPieChart';
import CalendarFilter from '../../common/calendar/CalendarFilter';

const LinkTapsView = ({ data = [], totalTaps: propTotalTaps = 0, onBack, dateRange, onDateRangeChange, onViewMore }) => {
    // State for local date filtering
    const [localDateRange, setLocalDateRange] = useState(
        dateRange || [new Date(new Date().setDate(new Date().getDate() - 30)), new Date()]
    );
    const [showCalendar, setShowCalendar] = useState(false);

    // Handle date range changes
    const handleDateRangeChange = useCallback((startDate, endDate) => {
        setLocalDateRange([startDate, endDate]);
        if (onDateRangeChange) {
            onDateRangeChange(startDate, endDate);
        }
    }, [onDateRangeChange]);

    // Format date range for display
    const formatDateRange = useCallback((range) => {
        const [start, end] = range;
        const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

        if (daysDiff <= 1) return 'Today';
        if (daysDiff <= 7) return 'Last 7 days';
        if (daysDiff <= 30) return 'Last 30 days';
        if (daysDiff <= 90) return 'Last 3 months';

        return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    }, []);

    // Calculate total taps consistently with pie chart - use prop if available
    const totalTaps = React.useMemo(() => {
        // First priority: use the prop value if provided
        if (propTotalTaps !== undefined && propTotalTaps !== null && propTotalTaps > 0) {
            return propTotalTaps;
        }

        if (!data || !Array.isArray(data) || data.length === 0) {
            return 0;
        }

        // If data is already aggregated (from linkTaps in backend response), sum the counts
        if (data[0] && (data[0].count !== undefined || data[0].linkType)) {
            return data.reduce((sum, item) => sum + (item.count || 1), 0);
        }

        // If data is raw tap events, count all events
        return data.length;
    }, [data, propTotalTaps]);

    // Process the data for display
    const processedData = React.useMemo(() => {
        if (!data || !Array.isArray(data) || data.length === 0) {
            return [];
        }

        // If data is already aggregated (from linkTaps in backend response)
        if (data[0] && (data[0].count !== undefined || data[0].linkType)) {
            return data.map(item => ({
                type: item.linkType || item.type || 'Unknown',
                name: item.linkName || item.name || item.linkType || 'Unknown Link',
                count: item.count || 1,
                links: [item] // Wrap in array for consistency
            }));
        }

        // If data is raw tap events, group by link type
        const groupedByType = data.reduce((acc, link) => {
            const type = link.linkType || link.type || 'Unknown';

            if (!acc[type]) {
                acc[type] = {
                    type: type,
                    count: 0,
                    name: link.linkName || link.linkTitle || type,
                    links: []
                };
            }

            acc[type].count += 1;
            acc[type].links.push(link);

            return acc;
        }, {});

        // Convert to array and sort by count (descending)
        return Object.values(groupedByType)
            .sort((a, b) => b.count - a.count);
    }, [data]);

    return (
        <Box sx={{ padding: '20px' }}>
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    minHeight: '40px',
                    alignItems: 'center',
                    padding: '30px 30px 15px 30px',
                    backgroundColor: '#F7F7F7',
                    zIndex: 11,
                    borderRadius: '8px'
                }}
            >
                <Box
                    onClick={onBack}
                    sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        borderRight: '1px solid #E0E0E0',
                        marginRight: '20px',
                        paddingRight: '12px'
                    }}
                >
                    <KeyboardArrowLeftIcon sx={{ color: '#828282' }} />
                </Box>                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Typography sx={{ fontSize: '20px', fontWeight: 500 }}>
                            Link Taps
                        </Typography>
                        <Tooltip title="Details about which links were tapped and how often">
                            <InfoIcon sx={{ fontSize: 16, color: '#828282' }} />
                        </Tooltip>
                    </Box>
                    {/* Display total taps count for consistency with StatsGrid */}
                    <Typography sx={{
                        fontSize: '14px',
                        color: '#666666',
                        marginLeft: '12px',
                        fontWeight: 500
                    }}>
                        Total: {totalTaps} taps
                    </Typography>
                </Box>                {/* Show date filter only when onViewMore prop is present (i.e., on main page) */}
                {onViewMore && (
                    <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                        <Button
                            variant="outlined"
                            startIcon={<CalendarTodayIcon />}
                            onClick={() => setShowCalendar(true)}
                            data-calendar-button
                            sx={{
                                height: '32px',
                                color: '#828282',
                                borderColor: '#E0E0E0',
                                textTransform: 'none',
                                fontSize: '14px',
                                fontWeight: 400,
                                padding: '6px 12px',
                                minWidth: 'auto',
                                '&:hover': {
                                    borderColor: '#CCCCCC',
                                    backgroundColor: '#F5F5F5'
                                }
                            }}
                        >
                            {formatDateRange(localDateRange)}
                        </Button>
                    </Box>
                )}
            </Box>            {/* Calendar Filter - Show component conditionally, only on main page */}
            {showCalendar && onViewMore && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 1000,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                    onClick={() => setShowCalendar(false)}
                >
                    <Box
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                            backgroundColor: 'white',
                            borderRadius: '16px',
                            padding: '20px',
                            boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.3)',
                            maxWidth: '400px',
                            maxHeight: '500px',
                            position: 'relative'
                        }}
                    >
                        {/* Close button */}
                        <Box
                            onClick={() => setShowCalendar(false)}
                            sx={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                backgroundColor: '#f5f5f5',
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: '#e0e0e0'
                                }
                            }}
                        >
                            <Typography sx={{ fontSize: '16px', color: '#666' }}>×</Typography>
                        </Box>
                        <CalendarFilter
                            onDateRangeChange={(startDate, endDate) => {
                                handleDateRangeChange(startDate, endDate);
                                setShowCalendar(false);
                            }}
                            initialDateRange={localDateRange}
                        />
                    </Box>
                </Box>
            )}

            <Box sx={{ marginTop: '24px' }}>
                {processedData.length > 0 ? (
                    <>                        {/* Pie Chart Section */}
                        <Box sx={{ marginBottom: '24px' }}>
                            <LinkTapsPieChart data={data} totalTaps={propTotalTaps} />
                        </Box>

                        {/* Table Section */}
                        <Box
                            sx={{
                                backgroundColor: '#ffffff',
                                borderRadius: '18px',
                                padding: '32px 18px',
                                minHeight: '400px'
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '20px'
                                }}
                            >
                                <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#333333' }}>
                                    Detailed Breakdown
                                </Typography>
                                <Tooltip title="Detailed list of all link taps">
                                    <InfoIcon sx={{ fontSize: 16, color: '#828282' }} />
                                </Tooltip>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    padding: '8px 16px',
                                    borderBottom: '1px solid #E0E0E0',
                                    backgroundColor: '#F8F8F8',
                                    borderRadius: '8px 8px 0 0',
                                    marginBottom: '16px'
                                }}
                            >
                                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#4F4F4F', width: '40%' }}>
                                    Link Type
                                </Typography>
                                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#4F4F4F', width: '40%' }}>
                                    Details
                                </Typography>
                                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#4F4F4F', width: '20%', textAlign: 'center' }}>
                                    Taps
                                </Typography>
                            </Box>

                            {processedData.map((group, index) => (
                                <Box key={index}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            padding: '12px 16px',
                                            borderBottom: '1px solid #F0F0F0',
                                            backgroundColor: index % 2 === 0 ? '#FAFAFA' : '#FFFFFF'
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#333333', width: '40%' }}>
                                            {group.name}
                                        </Typography>
                                        <Typography sx={{ fontSize: '14px', color: '#4F4F4F', width: '40%' }}>
                                            {group.links[0]?.details?.linkUrl || 'N/A'}
                                        </Typography>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#333333', width: '20%', textAlign: 'center' }}>
                                            {group.count}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </>
                ) : (
                    <Box
                        sx={{
                            backgroundColor: '#ffffff',
                            borderRadius: '18px',
                            padding: '32px 18px',
                            minHeight: '400px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <Typography sx={{ fontSize: '16px', color: '#828282' }}>
                            No link taps data available
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default LinkTapsView;